import type { StateCreator } from 'zustand'
import type {
  PlayoutStateRecord, WatchdogAlert, LiveSource,
  InterludeClip, ItemStatus
} from '../../types/index.js'

// ─── Slice ────────────────────────────────────────────────────────────────────

export interface PlayoutSlice {
  // State — connexion WebSocket
  wsConnected:     boolean
  wsStateReady:    boolean
  wsChannelId:     string | null
  wsEverReady:     boolean
  wsError:         string | null

  // State — état playout (miroir du PlayoutState BDD)
  playoutState:    PlayoutStateRecord | null
  playoutLoading:  boolean

  // State — alertes watchdog (FIFO, max 50)
  alerts:          WatchdogAlert[]
  unreadAlertCount: number

  // State — sources live
  liveSources:     LiveSource[]
  liveSourcesLoading: boolean

  // State — clips d'interlude disponibles
  interludeClips:  InterludeClip[]

  // State — contrôles manuels en cours
  skipPending:         boolean
  interludePending:    boolean
  emergencyPending:    boolean
  bugPending:          boolean

  // State — habillage
  activeBugType:       string | null

  // Actions — WebSocket
  setWsConnected:  (connected: boolean) => void
  setWsStateReady: (channelId: string) => void
  setWsError:      (err: string | null) => void

  // Actions — état playout
  setPlayoutState: (state: PlayoutStateRecord) => void
  patchPlayoutState: (patch: Partial<PlayoutStateRecord>) => void
  setPlayoutLoading: (loading: boolean) => void

  // Actions — alertes
  addAlert:        (alert: WatchdogAlert) => void
  dismissAlert:    (type: string, itemId?: string) => void
  clearAlerts:     () => void
  markAlertsRead:  () => void

  // Actions — sources live
  setLiveSources:     (sources: LiveSource[]) => void
  updateSourceStatus: (id: string, status: LiveSource['status']) => void

  // Actions — interludes
  setInterludeClips:  (clips: InterludeClip[]) => void

  // Actions — contrôles (mis à jour pendant les requêtes API)
  setSkipPending:      (p: boolean) => void
  setInterludePending: (p: boolean) => void
  setEmergencyPending: (p: boolean) => void
  setBugPending:       (p: boolean) => void

  // Actions — habillage
  setActiveBugType:    (type: string | null) => void

  // State — alerte fin de direct imminente
  liveEndingAlert: { itemId: string; sourceName: string; endsInSec: number } | null
  setLiveEndingAlert: (alert: { itemId: string; sourceName: string; endsInSec: number } | null) => void

  // Sélecteurs
  hasActiveAlert:    () => boolean
  criticalAlerts:    () => WatchdogAlert[]
  currentItemProgress: () => number   // 0-100
}

const MAX_ALERTS = 50

export const createPlayoutSlice: StateCreator<PlayoutSlice, [], [], PlayoutSlice> = (set, get) => ({
  // ── État initial ─────────────────────────────────────────────────────────
  wsConnected:         false,
  wsStateReady:        false,
  wsChannelId:         null,
  wsEverReady:         false,
  wsError:             null,
  playoutState:        null,
  playoutLoading:      false,
  alerts:              [],
  unreadAlertCount:    0,
  liveSources:         [],
  liveSourcesLoading:  false,
  interludeClips:      [],
  skipPending:         false,
  interludePending:    false,
  emergencyPending:    false,
  bugPending:          false,
  activeBugType:       null,
  liveEndingAlert:     null,

  // ── WebSocket ─────────────────────────────────────────────────────────────
  setWsConnected: (connected) => set({
    wsConnected:  connected,
    wsStateReady: false,
    wsChannelId:  null,
    wsError:      connected ? null : 'Déconnecté',
    // Une rupture invalide immédiatement le miroir temps réel. Les vues ne
    // peuvent ainsi jamais continuer à lire un état d'antenne ancien.
    ...(connected ? {} : {
      playoutState:    null,
      activeBugType:   null,
      liveEndingAlert: null,
    }),
  }),

  setWsStateReady: (channelId) => set({
    wsStateReady: true,
    wsChannelId:  channelId,
    wsEverReady:  true,
  }),

  setWsError: (err) => set({ wsError: err }),

  // ── Playout state ─────────────────────────────────────────────────────────
  setPlayoutState: (state) => set({ playoutState: state }),

  patchPlayoutState: (patch) =>
    set(state => ({
      playoutState: state.playoutState
        ? { ...state.playoutState, ...patch }
        : null,
    })),

  setPlayoutLoading: (loading) => set({ playoutLoading: loading }),

  // ── Alertes watchdog ──────────────────────────────────────────────────────
  addAlert: (alert) =>
    set(state => {
      // Déduplique : une seule alerte par (type + itemId)
      const filtered = state.alerts.filter(a =>
        !(a.type === alert.type && a.itemId === alert.itemId)
      )
      const alerts = [alert, ...filtered].slice(0, MAX_ALERTS)
      const unreadAlertCount = state.unreadAlertCount + (alert.severity !== 'OK' ? 1 : 0)
      return { alerts, unreadAlertCount }
    }),

  dismissAlert: (type, itemId) =>
    set(state => ({
      alerts: state.alerts.filter(a => !(a.type === type && a.itemId === itemId)),
    })),

  clearAlerts:    () => set({ alerts: [], unreadAlertCount: 0 }),
  markAlertsRead: () => set({ unreadAlertCount: 0 }),

  // ── Sources live ──────────────────────────────────────────────────────────
  setLiveSources: (sources) => set({ liveSources: sources }),

  updateSourceStatus: (id, status) =>
    set(state => ({
      liveSources: state.liveSources.map(s =>
        s.id === id ? { ...s, status } : s
      ),
    })),

  // ── Interludes ────────────────────────────────────────────────────────────
  setInterludeClips: (clips) => set({ interludeClips: clips }),

  // ── Contrôles ─────────────────────────────────────────────────────────────
  setSkipPending:       (p) => set({ skipPending: p }),
  setInterludePending:  (p) => set({ interludePending: p }),
  setEmergencyPending:  (p) => set({ emergencyPending: p }),
  setBugPending:        (p) => set({ bugPending: p }),

  // ── Habillage ─────────────────────────────────────────────────────────────
  setActiveBugType: (type) => set({ activeBugType: type }),

  // ── Alerte fin de direct ──────────────────────────────────────────────────
  setLiveEndingAlert: (alert) => set({ liveEndingAlert: alert }),

  // ── Sélecteurs ────────────────────────────────────────────────────────────
  hasActiveAlert: () =>
    get().alerts.some(a => ['CRITICAL', 'ERROR'].includes(a.severity)),

  criticalAlerts: () =>
    get().alerts.filter(a => ['CRITICAL', 'ERROR'].includes(a.severity)),

  currentItemProgress: () => {
    const s = get().playoutState
    if (!s || !s.currentItemId) return 0
    // Cette valeur est mise à jour par le composant MonitorPanel (tick 1s)
    // car Zustand n'est pas fait pour du state haute fréquence.
    return 0
  },
})
